import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import * as patientApi from "@/api/patient";
import type { PatientenfehlerResponseDTO } from "@/api/types";

interface PatientFehlerTabProps {
  patientId: number | undefined;
}

const PatientFehlerTab = ({ patientId }: PatientFehlerTabProps) => {
  const [fehlerList, setFehlerList] = useState<PatientenfehlerResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) {
      setFehlerList([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    patientApi
      .getFehler(patientId)
      .then((data) => {
        if (!cancelled) {
          setFehlerList(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to fetch Patientenfehler:", err);
          setError("Fehler beim Laden der Fehler und Hinweise.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (!patientId) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">
          Fehler und Hinweise werden erst nach dem Speichern angezeigt.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  const fehler = fehlerList.filter((f) => f.typ === "Fehler");
  const hinweise = fehlerList.filter((f) => f.typ === "Hinweis");

  if (fehler.length === 0 && hinweise.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">
          Keine Fehler oder Hinweise vorhanden.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {fehler.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="h6">Fehler</Typography>
            <Chip label={fehler.length} color="error" size="small" />
          </Box>
          <List dense>
            {fehler.map((f) => (
              <ListItem key={f.id} sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ErrorIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label={f.fehlercode} size="small" variant="outlined" color="error" />
                      <Typography variant="body2">{f.beschreibung}</Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {fehler.length > 0 && hinweise.length > 0 && <Divider />}

      {hinweise.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="h6">Hinweise</Typography>
            <Chip label={hinweise.length} color="info" size="small" />
          </Box>
          <List dense>
            {hinweise.map((f) => (
              <ListItem key={f.id} sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <InfoIcon color="info" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label={f.fehlercode} size="small" variant="outlined" color="info" />
                      <Typography variant="body2">{f.beschreibung}</Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Stack>
  );
};

export default PatientFehlerTab;
